export type Language = 'zh' | 'en'

export interface I18nData {
  manualTest: {
    entryTime: string
    exitTime: string
    direction: string
    long: string
    short: string
    entryPrice: string
    exitPrice: string
    positionSize: string
    pnl: string
    rValue: string
    manualOverride: string
    addNote: string
    exportCSV: string
    exportPDF: string
    addTradeRecord: string
    tradeRecords: string
    noTradeRecords: string
    totalTrades: string
    winRate: string
    avgRValue: string
    totalPnL: string
    allDirections: string
    allPnL: string
    profit: string
    loss: string
    estimatedPnL: string
    estimatedRValue: string
    cancel: string
    addRecord: string
    delete: string
    confirmDelete: string
    time: string
    price: string
    operation: string
    manualIntervention: string
    loading: string
    submitFailed: string
    deleteFailed: string
    getDataFailed: string
    getStatsFailed: string
    charts: {
      rValueDistribution: string
      pnlDistribution: string
      equityCurve: string
      rValueRange: string
      tradeCount: string
      cumulativePnL: string
      tradeNumber: string
      pnlAmount: string
    }
    export: {
      fileName: string
      strategyName: string
      testPeriod: string
      totalTrades: string
      winRate: string
      avgRValue: string
      maxDrawdown: string
      totalPnL: string
      tradeDetails: string
      notes: string
      generatedBy: string
      generatedAt: string
    }
  }
}

export const i18nData: Record<Language, I18nData> = {
  zh: {
    manualTest: {
      entryTime: '入场时间',
      exitTime: '出场时间',
      direction: '交易方向',
      long: '多头',
      short: '空头',
      entryPrice: '入场价格',
      exitPrice: '出场价格',
      positionSize: '仓位大小',
      pnl: '盈亏金额',
      rValue: 'R值',
      manualOverride: '主观干预',
      addNote: '添加备注',
      exportCSV: '导出CSV',
      exportPDF: '导出PDF',
      addTradeRecord: '添加交易记录',
      tradeRecords: '交易记录',
      noTradeRecords: '暂无交易记录',
      totalTrades: '总交易',
      winRate: '胜率',
      avgRValue: '平均R值',
      totalPnL: '总盈亏',
      allDirections: '所有方向',
      allPnL: '所有盈亏',
      profit: '盈利',
      loss: '亏损',
      estimatedPnL: '预计盈亏',
      estimatedRValue: '预计R值',
      cancel: '取消',
      addRecord: '添加记录',
      delete: '删除',
      confirmDelete: '确定要删除这条交易记录吗？',
      time: '时间',
      price: '价格',
      operation: '操作',
      manualIntervention: '⚠️ 主观干预',
      loading: '加载中...',
      submitFailed: '提交交易记录失败',
      deleteFailed: '删除交易记录失败',
      getDataFailed: '获取交易记录失败',
      getStatsFailed: '获取统计数据失败',
      charts: {
        rValueDistribution: 'R值分布直方图',
        pnlDistribution: '盈亏金额分布图',
        equityCurve: '资金曲线图',
        rValueRange: 'R值区间',
        tradeCount: '交易数量',
        cumulativePnL: '累计盈亏',
        tradeNumber: '交易编号',
        pnlAmount: '盈亏金额'
      },
      export: {
        fileName: 'manual_trades_{strategy_name}_{date}.csv',
        strategyName: '策略名称',
        testPeriod: '测试期间',
        totalTrades: '总交易笔数',
        winRate: '胜率',
        avgRValue: '平均R值',
        maxDrawdown: '最大回撤',
        totalPnL: '总盈亏',
        tradeDetails: '交易明细',
        notes: '备注',
        generatedBy: '生成人',
        generatedAt: '生成时间'
      }
    }
  },
  en: {
    manualTest: {
      entryTime: 'Entry Time',
      exitTime: 'Exit Time',
      direction: 'Direction',
      long: 'Long',
      short: 'Short',
      entryPrice: 'Entry Price',
      exitPrice: 'Exit Price',
      positionSize: 'Position Size',
      pnl: 'Profit / Loss',
      rValue: 'R Value',
      manualOverride: 'Manual Override',
      addNote: 'Add Note',
      exportCSV: 'Export CSV',
      exportPDF: 'Export PDF',
      addTradeRecord: 'Add Trade Record',
      tradeRecords: 'Trade Records',
      noTradeRecords: 'No trade records',
      totalTrades: 'Total Trades',
      winRate: 'Win Rate',
      avgRValue: 'Avg R Value',
      totalPnL: 'Total PnL',
      allDirections: 'All Directions',
      allPnL: 'All PnL',
      profit: 'Profit',
      loss: 'Loss',
      estimatedPnL: 'Estimated PnL',
      estimatedRValue: 'Estimated R Value',
      cancel: 'Cancel',
      addRecord: 'Add Record',
      delete: 'Delete',
      confirmDelete: 'Are you sure you want to delete this trade record?',
      time: 'Time',
      price: 'Price',
      operation: 'Operation',
      manualIntervention: '⚠️ Manual Intervention',
      loading: 'Loading...',
      submitFailed: 'Failed to submit trade record',
      deleteFailed: 'Failed to delete trade record',
      getDataFailed: 'Failed to get trade records',
      getStatsFailed: 'Failed to get statistics',
      charts: {
        rValueDistribution: 'R Value Distribution',
        pnlDistribution: 'PnL Distribution',
        equityCurve: 'Equity Curve',
        rValueRange: 'R Value Range',
        tradeCount: 'Trade Count',
        cumulativePnL: 'Cumulative PnL',
        tradeNumber: 'Trade Number',
        pnlAmount: 'PnL Amount'
      },
      export: {
        fileName: 'manual_trades_{strategy_name}_{date}.csv',
        strategyName: 'Strategy Name',
        testPeriod: 'Test Period',
        totalTrades: 'Total Trades',
        winRate: 'Win Rate',
        avgRValue: 'Average R Value',
        maxDrawdown: 'Max Drawdown',
        totalPnL: 'Total PnL',
        tradeDetails: 'Trade Details',
        notes: 'Notes',
        generatedBy: 'Generated By',
        generatedAt: 'Generated At'
      }
    }
  }
}

export const useI18n = (language: Language = 'zh') => {
  return i18nData[language]
} 